import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Champ copiable, pensé mobile-first : un tap sur le bouton copie la valeur
 * dans le presse-papier avec un feedback visuel immédiat (icône ✓ + toast).
 *
 * Usage :
 *   <Copyable value="0612345678">
 *     <a href="tel:0612345678">06 12 34 56 78</a>
 *   </Copyable>
 *
 * Si aucun `children` fourni, la `value` est rendue directement en text-slate-900.
 * `label` (optionnel) enrichit le toast : "Téléphone copié" au lieu de "Copié".
 *
 * Note : on utilise la Clipboard API moderne. Fallback silencieux si non dispo
 * (ex. contexte non-HTTPS ou navigateur très ancien).
 */
export function Copyable({ value, label, children, className = '', testId }) {
    const [copied, setCopied] = useState(false);

    if (value == null || value === '') return null;

    const handleCopy = async (e) => {
        e.stopPropagation();
        e.preventDefault();
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(String(value));
            } else {
                // Fallback pour anciens contextes
                const ta = document.createElement('textarea');
                ta.value = String(value);
                ta.style.position = 'fixed';
                ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.focus();
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            }
            setCopied(true);
            toast.success(label ? `${label} copié` : 'Copié dans le presse-papier');
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error('Impossible de copier');
        }
    };

    return (
        <span className={`inline-flex items-center gap-1.5 min-w-0 max-w-full ${className}`}>
            <span className="min-w-0 break-all">
                {children ?? <span className="text-slate-900">{value}</span>}
            </span>
            <button
                type="button"
                onClick={handleCopy}
                aria-label={label ? `Copier ${label.toLowerCase()}` : 'Copier'}
                title={label ? `Copier ${label.toLowerCase()}` : 'Copier'}
                data-testid={testId}
                className={`shrink-0 inline-flex items-center justify-center rounded p-1 -m-1 transition-colors ${copied ? 'text-emerald-600' : 'text-slate-400 hover:text-purple-600 hover:bg-purple-50'} active:scale-90`}
            >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
        </span>
    );
}

export default Copyable;
